import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  Info,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useDistributionSummary } from "../../hooks";
import { DistributionTable } from "./DistributionTable";
import { PageHeader } from "@shared/components/PageHeader";

interface DistributionTabProps {
  onCycleSelect: (cycle: any) => void;
}

export function DistributionTab({ onCycleSelect }: DistributionTabProps) {
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const { data: distributionSummary } = useDistributionSummary();
  const handleMetricClick = (metric: string) => {
    setSelectedMetric((prev) => (prev === metric ? null : metric));
  };

  // KPI Cards configuration using QuickStatsCard
  const distributionKpiCards = [
    {
      title: "Pending Dispatch",
      count: distributionSummary?.pendingDispatch,
      trendSummary: "Bills awaiting dispatch to consumers",
      trendDirection: "down" as const,
      icon: <Clock className="h-8 w-8 text-yellow-500" />,
      isSelected: selectedMetric === "pendingDispatch",
    },
    {
      title: "Delivery Failures",
      count: distributionSummary?.deliveryFailure,
      trendSummary: "Failed bill delivery attempts ",
      trendDirection: "down" as const,
      icon: <XCircle className="h-8 w-8 text-red-500" />,
      isSelected: selectedMetric === "deliveryFailures",
    },
    {
      title: "Success Rate",
      count: distributionSummary?.distributionSuccessPct,
      trendSummary: "Percentage of successful bill deliveries",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
      isSelected: selectedMetric === "distributionSuccessRate",
    },
    {
      title: "Missing Contact Info",
      count: distributionSummary?.missingContactInfo,
      trendSummary: "Consumers with incomplete contact details",
      trendDirection: "down" as const,
      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Dispatch Delayed",
      count: distributionSummary?.delayedPrintAlerts,
      trendSummary: "Bills with delayed dispatch schedules",
      trendDirection: "down" as const,
      icon: <Calendar className="h-8 w-8 text-purple-500" />,
      isSelected: selectedMetric === "dispatchDelayed",
    },
  ];

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <PageHeader
            title="Distribution"
            subtitle="Distribution records and delivery tracking"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {distributionKpiCards.map((card, index) => (
          <QuickStatsCard
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      <Alert variant="default" className="bg-muted border border-input">
        <Info className="h-4 w-4" />
        <AlertTitle>Tip</AlertTitle>
        <AlertDescription className="text-xs">
          Click a cycle to manage consumer distribution. Use Actions for editing
          dispatch date or jumping to ZIP download.
        </AlertDescription>
      </Alert>

      <DistributionTable
        activeFilter={selectedMetric}
        onCycleClick={onCycleSelect}
      />
    </div>
  );
}
